import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LogOut,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';
import API_CONFIG from '../config/api';
import { getAppVersion, getSidebarTitle } from '../config/appConfig';
import { getMenuIcon, getMenuLabel, getRoleLabel } from '../utils/menuLabels';

function getInitials(name) {
  if (!name) return 'U';
  return String(name)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [activeSubmenuItem, setActiveSubmenuItem] = useState(null);
  const [allMenus, setAllMenus] = useState([]);
  const location = useLocation();
  const { user, logout, token } = useAuth();

  useEffect(() => {
    const loadMenus = async () => {
      if (!token || !user) return;

      try {
        const response = await apiService.authenticatedRequest(
          API_CONFIG.ENDPOINTS.USERS.MENUS,
          { method: 'GET' }
        );

        if (response.success) {
          setAllMenus(response.data || []);
        }
      } catch (error) {
        console.error('Erro ao carregar menus:', error);
      }
    };

    loadMenus();
  }, [token, user]);

  useEffect(() => {
    const currentPath = location.pathname;

    if (currentPath.startsWith('/boletim/')) {
      setActiveSubmenu('boletim');
      if (currentPath === '/boletim/producao-bpa') setActiveSubmenuItem('producao-bpa');
      else if (currentPath === '/boletim/gera-bpa') setActiveSubmenuItem('gera-bpa');
      else if (currentPath === '/boletim/pacientes-incompletos') setActiveSubmenuItem('pacientes-incompletos');
    } else if (currentPath.startsWith('/bpa/')) {
      setActiveSubmenu('bpa');
      if (currentPath === '/bpa/viagem' || currentPath === '/boletim/bpa-transporte') {
        setActiveSubmenuItem('bpa-viagem');
      }
    } else if (currentPath.startsWith('/transport/')) {
      setActiveSubmenu('transport');
      const segment = currentPath.split('/').filter(Boolean).pop();
      setActiveSubmenuItem(segment ? `transport-${segment}` : 'transport-rides');
    } else if (currentPath === '/usuarios') {
      setActiveSubmenu(null);
      setActiveSubmenuItem(null);
    } else {
      setActiveSubmenu(null);
      setActiveSubmenuItem(null);
    }
  }, [location.pathname]);

  const getMenuItems = () => {
    if (!user || !allMenus.length) return [];

    let allowedMenuIds = [];
    if (user.role === 'admin') {
      allowedMenuIds = allMenus.map((menu) => menu.id);
    } else if (user.allowedMenus?.length) {
      allowedMenuIds = user.allowedMenus.map((menu) => menu.id);
    } else {
      return [];
    }

    const menuMap = new Map();
    allMenus.forEach((menu) => {
      menuMap.set(menu.id, {
        ...menu,
        id: menu.name || `menu-${menu.id}`,
        submenu: [],
      });
    });

    const rootMenus = [];
    allMenus.forEach((menu) => {
      if (!allowedMenuIds.includes(menu.id)) return;
      const menuItem = menuMap.get(menu.id);
      if (!menu.parent_id) {
        rootMenus.push(menuItem);
      } else {
        const parentMenu = menuMap.get(menu.parent_id);
        if (parentMenu) parentMenu.submenu.push(menuItem);
      }
    });

    return rootMenus.filter((menu) => menu.path || (menu.submenu && menu.submenu.length > 0));
  };

  const menuItems = getMenuItems();
  const sidebarWidthClass = isOpen ? 'w-64' : 'w-[72px]';

  const renderIcon = (menuName) => {
    const Icon = getMenuIcon(menuName);
    return <Icon size={18} strokeWidth={1.8} />;
  };

  return (
    <aside className={`fixed inset-y-0 left-0 z-40 flex flex-col border-r border-border bg-white transition-all duration-300 ${sidebarWidthClass}`}>
      <div className="flex items-center justify-between border-b border-border px-4 py-4">
        <div className="flex items-center gap-3 overflow-hidden">
          <img
            src={`${process.env.PUBLIC_URL}/assets/logo.svg`}
            alt="Logo"
            className="h-9 w-9 shrink-0 object-contain"
          />
          {isOpen && (
            <span className="truncate text-sm font-semibold text-text">
              {getSidebarTitle()}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted hover:bg-slate-50"
          aria-label="Alternar menu"
        >
          {isOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1">
          {menuItems.map((item) => {
            const hasSubmenu = item.submenu?.length > 0;
            const isActiveRoot = activeSubmenu === item.id || location.pathname === item.path;

            return (
              <li key={item.id}>
                {hasSubmenu ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setActiveSubmenu(activeSubmenu === item.id ? null : item.id)}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                        isActiveRoot
                          ? 'bg-primary-light text-primary'
                          : 'text-muted hover:bg-slate-50 hover:text-text'
                      }`}
                    >
                      <span className="shrink-0">{renderIcon(item.name)}</span>
                      {isOpen && <span className="truncate">{getMenuLabel(item)}</span>}
                      {isOpen && (
                        <ChevronDown
                          size={16}
                          className={`ml-auto transition ${activeSubmenu === item.id ? 'rotate-180' : ''}`}
                        />
                      )}
                    </button>
                    {isOpen && activeSubmenu === item.id && (
                      <ul className="mt-1 space-y-1 pl-3">
                        {item.submenu.map((subItem) => (
                          <li key={subItem.id}>
                            <Link
                              to={subItem.path}
                              onClick={() => setActiveSubmenuItem(subItem.id)}
                              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition ${
                                activeSubmenuItem === subItem.id
                                  ? 'bg-primary-light font-medium text-primary'
                                  : 'text-muted hover:bg-slate-50 hover:text-text'
                              }`}
                            >
                              <span className="shrink-0">{renderIcon(subItem.name || subItem.id)}</span>
                              {getMenuLabel(subItem)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path || '#'}
                    onClick={() => {
                      setActiveSubmenu(null);
                      setActiveSubmenuItem(null);
                    }}
                    className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                      location.pathname === item.path
                        ? 'bg-primary-light text-primary'
                        : 'text-muted hover:bg-slate-50 hover:text-text'
                    }`}
                  >
                    <span className="shrink-0">{renderIcon(item.name)}</span>
                    {isOpen && <span className="truncate">{getMenuLabel(item)}</span>}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="border-t border-border p-3">
        {isOpen ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-xl border border-border bg-slate-50 px-3 py-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
                {getInitials(user?.name || user?.username)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-text">
                  {user?.name || user?.username || 'Usuário'}
                </div>
                <div className="truncate text-xs text-muted">{getRoleLabel(user?.role)}</div>
              </div>
            </div>
            <div className="px-1 text-center text-[11px] font-medium tracking-wide text-muted">
              Versão {getAppVersion()}
            </div>
            <button
              type="button"
              onClick={logout}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-border px-3 py-2.5 text-sm font-medium text-muted transition hover:bg-slate-50 hover:text-text"
            >
              <LogOut size={16} />
              Sair
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div
              className="px-0.5 text-center text-[10px] font-medium leading-tight text-muted"
              title={`Versão ${getAppVersion()}`}
            >
              v{getAppVersion()}
            </div>
            <button
              type="button"
              onClick={logout}
              className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl border border-border text-muted hover:bg-slate-50"
              title="Sair"
            >
              <LogOut size={16} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
